import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import { ReportesService } from '../services/reportesService';
import { useEmpresasContext } from '../context/EmpresasContext';
import type { ReporteFiltros } from '../types';

// ===============================
// HOOK PARA LISTA DE REPORTES
// ===============================

export const useReportes = (filtros: ReporteFiltros = {}) => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  const queryClient = useQueryClient();
  
  // 🆕 NUEVO: Detectar si se debe refrescar la lista
  useEffect(() => {
    const shouldRefresh = localStorage.getItem('refreshReportesList');
    if (shouldRefresh === 'true') {
      // Limpiar el flag
      localStorage.removeItem('refreshReportesList');
      // Invalidar y refrescar la query
      queryClient.invalidateQueries(['reportes', empresaId]);
      // console.log('🔄 Lista de reportes refrescada después de crear nuevo reporte');
    }
  }, [queryClient, empresaId]);
  
  return useQuery(
    ['reportes', empresaId, filtros],
    () => ReportesService.getReportes(filtros),
    {
      enabled: !!empresaId, // Solo ejecutar si hay empresa activa
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutos
    }
  );
};

// ===============================
// HOOK PARA REPORTE INDIVIDUAL
// ===============================

export const useReporte = (id: number) => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['reporte', empresaId, id],
    () => ReportesService.getReporte(id),
    {
      enabled: !!empresaId && !!id,
    }
  );
};

// ===============================
// HOOK PARA CREAR REPORTE
// ===============================

export const useCrearReporte = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: any) => ReportesService.crearReporte(datos),
    {
      onSuccess: () => {
        // Invalidar todas las queries de reportes para la empresa actual
        queryClient.invalidateQueries(['reportes', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['reportes-stats', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOK PARA ACTUALIZAR REPORTE
// ===============================

export const useActualizarReporte = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: any }) =>
      ReportesService.actualizarReporte(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['reportes', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['reporte', empresaActiva?.empresa.id, id]);
        queryClient.invalidateQueries(['reportes-stats', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOK PARA ESTADÍSTICAS
// ===============================

export const useEstadisticasReportes = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['reportes-stats', empresaId],
    async () => {
      // Por ahora simulamos, luego reemplazar con endpoint real
      return {
        success: true,
        data: {
          total_reportes: 0,
          reportes_pendientes: 0,
          reportes_aprobados: 0,
          reportes_rechazados: 0,
          horas_trabajadas_mes: 0,
          area_trabajada_mes: 0,
          maquinas_activas: 0,
          operadores_activos: 0
        }
      };
    },
    {
      enabled: !!empresaId,
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  );
};

// ===============================
// HOOK PARA GENERAR PDF
// ===============================

export const useGenerarPDF = () => {
  return useMutation(
    ({ reporteId, opciones }: { reporteId: number; opciones?: any }) =>
      ReportesService.generarPDF(reporteId, opciones),
    {
      onSuccess: (response) => {
        if (response.pdf_url) {
          window.open(response.pdf_url, '_blank');
        }
      },
    }
  );
};

export default {
  useReportes,
  useReporte,
  useCrearReporte,
  useActualizarReporte,
  useEstadisticasReportes,
  useGenerarPDF,
};
